"use client";

import { getLocalTimeZone, today } from "@internationalized/date";
import { interpolate } from "@jetblue/kit-interpolation";
import NextImage from "next/image";
import { useMemo, useEffect } from "react";
import { Form } from "react-aria-components";
import type { processBagFees } from "./helper/process-bag-fees";
import { Button } from "@jetblue/kit/button";
import { FieldError } from "@jetblue/kit/field-error";
import { GhostCard } from "./ghost-card";
import { IcSearch } from "@jetblue/kit-icons/ic-search";
import { InputRoute } from "@jb-dotcom/dotcom-components/input-route";
import { InputSelect } from "@jetblue/kit/input-select";
import { SingleDatePicker } from "@jetblue/kit/date-picker";
import { Table } from "@jetblue/kit/table";
import { useBagCalculator } from "./use-bag-calculator";

const TODAY = today(getLocalTimeZone());
const DATE_PICKER_START_DATE = TODAY;
const DATE_PICKER_END_DATE = TODAY.add({ days: 331 });

const BLUE_PLUS_AVAILABLE_AIRPORTS = new Set([
  "CTG",
  "GYE",
  "MDE",
  "PAP",
  "GEO",
]);

export type BagCalculatorFormClientProperties = {
  bagCalculatorApiUrl: string;
  bagCalculatorSubscriptionKey: string;
  fareClassOptions: { label: string; value: string }[];
  labels: {
    close: string;
    departureDate: string;
    destination: string;
    done: string;
    errors: {
      departureDate: string;
      destination: string;
      fareClass: string;
      origin: string;
    };
    fareClass: string;
    origin: string;
    reset: string;
    submit: string;
    swap: string;
    swapAnnouncement: string;
  };
  apiResponseLabels: {
    bagTypes: {
      carryOn: string;
      checkedBagOne: string;
      checkedBagTwo: string;
      includedBag: string;
      personalItem: string;
    };
    heading: string;
    screenReaderRequestStatus: {
      error: string;
      loading: string;
      success: string;
    };
    tableHeadings: {
      columnOne: string;
      columnThree: string;
      columnTwo: string;
    };
  };
  error: {
    message: string;
    imageSrc: string;
    imageAlt: string;
  };
};
export const BagCalculatorFormClient = ({
  apiResponseLabels,
  bagCalculatorApiUrl,
  bagCalculatorSubscriptionKey,
  error,
  fareClassOptions,
  labels,
}: BagCalculatorFormClientProperties) => {
  const {
    destination,
    fareClassSelect,
    handleSubmit,
    origin,
    query,
    selectedDate,
    setDestination,
    setOrigin,
    setSelectedDate,
    showValidation,
    usOdData,
  } = useBagCalculator({
    apiUrl: bagCalculatorApiUrl,
    fareClassOptions,
    includedBagLabel: apiResponseLabels.bagTypes.includedBag,
    subscriptionKey: bagCalculatorSubscriptionKey,
  });

  const filteredFareClassOptions = useMemo(() => {
    const isBluePlusAvailable =
      BLUE_PLUS_AVAILABLE_AIRPORTS.has(origin) ||
      BLUE_PLUS_AVAILABLE_AIRPORTS.has(destination);

    if (!isBluePlusAvailable) {
      return fareClassOptions.filter(
        (option) => option.value.toLowerCase() !== "blue plus",
      );
    }

    return fareClassOptions;
  }, [fareClassOptions, origin, destination]);

  useEffect(() => {
    const isSelectedFareClassAvailable = filteredFareClassOptions.some(
      (option) => option.value === fareClassSelect.value,
    );

    if (!isSelectedFareClassAvailable) {
      const nextValue = filteredFareClassOptions[0]?.value ?? null;
      fareClassSelect.onSelectionChange(nextValue);
    }
  }, [
    filteredFareClassOptions,
    fareClassSelect.value,
    fareClassSelect.onSelectionChange,
  ]);

  const bagFees = query.data as ReturnType<typeof processBagFees> | null;

  return (
    <>
      <div aria-live="polite" className="sr-only" role="status">
        {query.isFetching && (
          <span>{apiResponseLabels.screenReaderRequestStatus.loading}</span>
        )}
        {query.isError && (
          <span>{apiResponseLabels.screenReaderRequestStatus.error}</span>
        )}
      </div>

      <Form
        className="
          mt-10 flex w-full flex-col gap-1

          lg:flex-row lg:items-start
        "
        onSubmit={handleSubmit}
        validationBehavior="aria"
      >
        <div
          className="
            flex w-full flex-col gap-1

            lg:flex-row
          "
        >
          <InputRoute
            className="
              w-full

              lg:w-3/5
            "
            destination={destination}
            destinationIsRequired
            labels={{
              close: labels.close,
              destination: labels.destination,
              errors: labels.errors,
              origin: labels.origin,
              swap: labels.swap,
              swapAnnouncement: labels.swapAnnouncement,
            }}
            odRouteData={{ destination: usOdData, origin: usOdData }}
            onDestinationChange={setDestination}
            onOriginChange={setOrigin}
            onSwapClick={() => {
              const currentOrigin = origin;
              const currentDestination = destination;
              setOrigin(currentDestination);
              setDestination(currentOrigin);
            }}
            origin={origin}
            originIsRequired
            showValidation={showValidation}
          />

          <div
            className="
              w-full

              lg:w-56
            "
          >
            <SingleDatePicker
              endDate={DATE_PICKER_END_DATE}
              error={
                <FieldError>
                  {({ isInvalid }) =>
                    isInvalid ? labels.errors.departureDate : ""
                  }
                </FieldError>
              }
              isRequired
              labels={{
                close: labels.close,
                done: labels.done,
                input: labels.departureDate,
                reset: labels.reset,
              }}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              showValidation={showValidation}
              startDate={DATE_PICKER_START_DATE}
            />
          </div>

          <div
            className="
              w-full

              lg:w-56
            "
          >
            <InputSelect
              isRequired
              {...fareClassSelect}
              className="relative"
              error={
                <FieldError>
                  {({ isInvalid }) =>
                    isInvalid ? labels.errors.fareClass : ""
                  }
                </FieldError>
              }
              label={labels.fareClass}
              options={filteredFareClassOptions}
              validationBehavior="aria"
              variant="standard"
            />
          </div>
        </div>

        <div
          className="
            flex flex-col gap-2

            lg:mt-0
          "
        >
          <Button
            aria-label={
              query.isFetching
                ? apiResponseLabels.screenReaderRequestStatus.loading
                : labels.submit
            }
            className="
              mt-5 h-14

              lg:mt-0 lg:w-14 lg:rounded-s-none lg:px-0
            "
            isDisabled={query.isFetching}
            isFullWidth
            isLoading={query.isFetching}
            type="submit"
            variant="primary"
          >
            <IcSearch
              aria-hidden
              className="
                hidden

                lg:block
              "
              height={22}
              width={22}
            />
            <span
              className="
                text-xs-heading-extra-bold

                lg:hidden
              "
            >
              {labels.submit}
            </span>
          </Button>
        </div>
      </Form>

      {query.isError && (
        <div
          className="
            mt-20 flex min-h-96 flex-col items-center justify-center gap-6
            bg-off-white px-5 py-10
          "
        >
          {error.imageSrc && (
            <div className="relative size-16">
              <NextImage
                alt={error.imageAlt}
                className="object-contain"
                fill
                src={error.imageSrc}
              />
            </div>
          )}
          <p
            className="
              text-center text-body text-charcoal

              lg:px-60
            "
          >
            {error.message}
          </p>
        </div>
      )}

      <div className="my-20">
        {query.isFetching && <GhostCard />}

        {bagFees && (
          <div className="my-20">
            <h3 className="mb-10 text-s-heading font-semibold text-core-blue">
              {interpolate(apiResponseLabels.heading, {
                fareClass: fareClassSelect.value,
              })}
            </h3>

            <Table
              aria-label="Baggage fees"
              headers={[
                {
                  className: "bg-core-blue h-1",
                  label: (
                    <div className="p-4 text-start text-core-blue lg:text-center">
                      {apiResponseLabels.tableHeadings.columnOne}
                    </div>
                  ),
                },
                {
                  className: "bg-core-blue h-1",
                  label: (
                    <div className="p-4 text-start text-core-blue lg:text-center">
                      {apiResponseLabels.tableHeadings.columnTwo}
                    </div>
                  ),
                },
                {
                  className: "bg-core-blue h-1",
                  label: (
                    <div className="p-4 text-start text-core-blue lg:text-center">
                      {apiResponseLabels.tableHeadings.columnThree}
                    </div>
                  ),
                },
              ]}
              rows={[
                [
                  <div
                    className="
                      p-4 text-start font-extrabold text-charcoal

                      lg:text-center
                    "
                    key="r0c0"
                  >
                    {apiResponseLabels.bagTypes.personalItem}
                  </div>,
                  <div
                    className="
                      p-4 text-start

                      lg:text-center
                    "
                    key="r0c1"
                  >
                    {bagFees.personalItem.booking}
                  </div>,
                  <div
                    className="
                      p-4 text-start

                      lg:text-center
                    "
                    key="r0c2"
                  >
                    {bagFees.personalItem.checkin}
                  </div>,
                ],
                [
                  <div
                    className="
                      p-4 text-start font-extrabold text-charcoal

                      lg:text-center
                    "
                    key="r1c0"
                  >
                    {apiResponseLabels.bagTypes.carryOn}
                  </div>,
                  <div
                    className="
                      p-4 text-start text-charcoal

                      lg:text-center
                    "
                    key="r1c1"
                  >
                    {bagFees.carryOn.booking}
                  </div>,
                  <div
                    className="
                      p-4 text-start

                      lg:text-center
                    "
                    key="r1c2"
                  >
                    {bagFees.carryOn.checkin}
                  </div>,
                ],
                [
                  <div
                    className="
                      p-4 text-start font-extrabold text-charcoal

                      lg:text-center
                    "
                    key="r2c0"
                  >
                    {apiResponseLabels.bagTypes.checkedBagOne}
                  </div>,
                  <div
                    className="
                      p-4 text-start text-charcoal

                      lg:text-center
                    "
                    key="r2c1"
                  >
                    {bagFees.firstBag.booking}
                  </div>,
                  <div
                    className="
                      p-4 text-start text-charcoal

                      lg:text-center
                    "
                    key="r2c2"
                  >
                    {bagFees.firstBag.checkin}
                  </div>,
                ],
                [
                  <div
                    className="
                      p-4 text-start font-extrabold

                      lg:text-center
                    "
                    key="r3c0"
                  >
                    {apiResponseLabels.bagTypes.checkedBagTwo}
                  </div>,
                  <div
                    className="
                      p-4 text-start text-charcoal

                      lg:text-center
                    "
                    key="r3c1"
                  >
                    {bagFees.secondBag.booking}
                  </div>,
                  <div
                    className="
                      p-4 text-start text-charcoal

                      lg:text-center
                    "
                    key="r3c2"
                  >
                    {bagFees.secondBag.checkin}
                  </div>,
                ],
              ]}
              showRowDividers={false}
            />
          </div>
        )}
      </div>
    </>
  );
};
